/**
 * <ActivityCorrectionPoint> — un point de la correction annotée (molecule).
 *
 * La copie corrigée porte des pastilles numérotées ①②③, colorées par type ; cette carte dit ce que
 * chacune signale. En tête, la MÊME pastille que sur la copie (`<ActivityCorrectionMarker>`, sans
 * geste) : on reconnaît d'un coup d'œil de quel point on parle. À côté, le type en chip teintée
 * avec son glyphe ; puis la notion, en titre de la carte, et l'explication (markdown + formules,
 * par `<RichText>`).
 *
 * Une carte du livre V16 : blanche, sans filet, une seule ombre (`<Card>`). Le filet coloré de la
 * carte tuteur du legacy (`CorrectionPointCard.js`) n'est pas repris — c'est la chip qui porte le
 * type. La pastille « À vérifier » ne l'est pas non plus : elle dit au tuteur de relire un point,
 * elle n'apprend rien au parent.
 */
import React from "react"

import { type UIActivityCorrectionPoint } from "../../../ports/primary/ui/types"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { ActivityCorrectionMarker } from "../ActivityCorrectionMarker"
import { Card } from "../Card"
import { RichText } from "../RichText"
import { CORRECTION_POINT_FACES } from "./correctionPointFaces"

export type ActivityCorrectionPointProps = {
    point: UIActivityCorrectionPoint
    testID?: string
}

export function ActivityCorrectionPoint(props: ActivityCorrectionPointProps) {
    const { point, testID } = props
    const face = CORRECTION_POINT_FACES[point.type]

    return (
        <Card p="4" testID={testID}>
            <Stack gap="3">
                <Row gap="2">
                    <ActivityCorrectionMarker index={point.index} type={point.type} />
                    <Badge
                        text={face.label}
                        variant={face.chip}
                        // La lavande neutre est déjà une tinte : elle se pose telle quelle.
                        tinted={face.chip !== "neutral"}
                        iconName={face.icon}
                        truncate={false}
                    />
                </Row>
                <Stack gap="1">
                    <Text size="text-lg" weight="semibold">
                        {point.notion}
                    </Text>
                    {point.explanation != null && point.explanation !== "" && <RichText value={point.explanation} />}
                </Stack>
            </Stack>
        </Card>
    )
}
